import React, { useState, useEffect, ChangeEvent } from 'react';
import { articleService, IArticle } from '../../services/articleService';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';

const DashboardPage: React.FC = () => {
  const { user, profile } = useAuth();
  const [articles, setArticles] = useState<IArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile?.avatar_url || null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserArticles = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          setError("Authentication error. Please sign out and sign in again.");
          return;
        }
        const userArticles = await articleService.getByAuthor(user.id);
        setArticles(userArticles || []);
      } catch (e: any) {
        setError('A problem occurred while fetching your data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchUserArticles();
  }, []);

  // Handle avatar upload
  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    setMessage(null);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;
      const { error: uploadError } = await supabase.storage.from('article_images').upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('article_images').getPublicUrl(filePath);
      setAvatarUrl(data.publicUrl);
      // Update profile
      const { error: updateError } = await supabase.from('profiles').update({ avatar_url: data.publicUrl }).eq('id', user.id);
      if (updateError) throw updateError;
      setMessage('Profile photo updated!');
    } catch (err: any) {
      setError('Failed to upload photo: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (articleId: number) => {
    if (window.confirm('Are you sure you want to delete this article permanently?')) {
      const { error } = await supabase
        .from('articles')
        .delete()
        .match({ id: articleId });

      if (error) {
        alert('Error deleting article: ' + error.message);
      } else {
        setArticles(articles.filter(article => article.id !== articleId));
        alert('Article deleted successfully.');
      }
    }
  };

  if (loading) {
    return (
      <div className="utility-page min-h-screen flex items-center justify-center bg-white">
        <p role="status" className="text-sm text-gray-500">Loading your stories…</p>
      </div>
    );
  }

  return (
    <div className="utility-page min-h-screen bg-white text-black font-roboto-mono py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Section */}
        <div className="flex flex-col items-center mb-10">
          <div className="relative w-32 h-32 mb-4">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Profile" className="w-32 h-32 rounded-full object-cover border-4 border-black shadow" />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center text-5xl font-bold text-gray-400 border-4 border-black">
                {profile?.first_name?.[0]?.toUpperCase() || profile?.username?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <label className="absolute bottom-0 right-0 bg-black text-white rounded-full p-2 cursor-pointer  hover:bg-gray-800 transition-colors">
              <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} disabled={uploading} />
              <span className="text-xs">Edit</span>
            </label>
          </div>
          <h2 className="text-2xl font-bold mb-1">{profile?.first_name && profile?.last_name ? `${profile.first_name} ${profile.last_name}` : profile?.username}</h2>
          {message && <div className="text-green-600 mb-2">{message}</div>}
          {error && <div className="text-red-600 mb-2">{error}</div>}
        </div>

        {/* Onboarding Guidance */}
        {!profile?.is_writer && !profile?.is_admin && (
          <div className="bg-yellow-100 border border-yellow-300  p-6 text-center mb-8">
            <h2 className="text-xl font-bold mb-2">Become a Writer!</h2>
            <p className="text-gray-700 mb-4">To start writing articles for us, request writer access in your <Link to="/profile" className="underline text-brand-accent">profile settings</Link>.</p>
            <Link
              to="/profile"
              className="bg-black text-white px-6 py-3  hover:bg-gray-800 transition-colors font-medium"
            >
              Request Writer Access
            </Link>
          </div>
        )}

        {/* Start Writing CTA */}
        {(profile?.is_writer || profile?.is_admin) && (
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold">Writer Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage your articles, drafts, and published content.</p>
            </div>
            <Link to="/new-article">
              <button className="bg-gray-800 text-white px-4 py-2  hover:bg-gray-700 transition-colors flex items-center">
                + New Article
              </button>
            </Link>
          </div>
        )}

        {/* Articles Table */}
        {(profile?.is_writer || profile?.is_admin) && (
          <div className="bg-white border border-gray-200   overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Views
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {articles.length > 0 ? (
                    articles.map((article) => (
                      <tr key={article.id} className="hover:bg-white">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-black">{article.title}</div>
                          <div className="text-sm text-gray-500">{article.category}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              article.status === 'published'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}
                          >
                            {article.status === 'published' ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {article.views || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(article.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => navigate(`/edit/${article.id}`)}
                            className="text-brand-accent hover:text-opacity-80"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(article.id)}
                            className="text-red-500 hover:text-red-700 ml-4"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-10">
                        <h3 className="text-lg font-medium text-black">No articles found</h3>
                        <p className="text-gray-500 mt-1">
                          You haven't written any articles yet. Get started by creating one!
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;